import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Grid, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import { PlayArrowRounded, ScienceOutlined } from '@mui/icons-material';
import { Panel } from './ui';
import { errorMessage, tradingApi } from '../services/api';
import { BacktestRun } from '../types';
import { t } from '../utils/theme';

const iso = (d: Date) => d.toLocaleDateString('en-CA');
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };

const PRESETS = [
  { label: 'Design period 2015–2021', from: '2015-01-01', to: '2021-12-31' },
  { label: 'Hold-out 2022–today', from: '2022-01-01', to: iso(daysAgo(1)) },
  { label: 'Last 3 years', from: iso(daysAgo(3 * 365)), to: iso(daysAgo(1)) },
];

/** Start a backtest on the current Settings and follow its progress. Calls onFinished when a run completes. */
const BacktestPanel: React.FC<{ onFinished: () => void }> = ({ onFinished }) => {
  const [run, setRun] = useState<BacktestRun | null>(null);
  const [from, setFrom] = useState(PRESETS[0].from);
  const [to, setTo] = useState(PRESETS[0].to);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const wasRunning = useRef(false);

  const running = run?.status === 'QUEUED' || run?.status === 'RUNNING';

  const poll = useCallback(async () => {
    try {
      const r = (await tradingApi.backtestStatus()).run;
      setRun(r);
      const nowRunning = r?.status === 'QUEUED' || r?.status === 'RUNNING';
      if (wasRunning.current && !nowRunning) onFinished();
      wasRunning.current = nowRunning;
    } catch { /* keep last state; next poll retries */ }
  }, [onFinished]);

  useEffect(() => { poll(); }, [poll]);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
  }, [running, poll]);

  const start = async () => {
    setError(null);
    setStarting(true);
    try {
      setRun((await tradingApi.backtestStart(from, to)).run);
      wasRunning.current = true;
    } catch (err) {
      setError(errorMessage(err, 'Could not start the backtest'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Panel>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ p: 0.9, borderRadius: 2, bgcolor: 'rgba(99,102,241,0.14)', color: t.accentSoft, display: 'flex' }}><ScienceOutlined /></Box>
            <Box>
              <Typography variant="h6">Backtest</Typography>
              <Typography variant="body2" sx={{ color: t.text2 }}>
                Replays daily closes through the same monthly rebalance the live strategy uses, with your current Settings.
              </Typography>
            </Box>
          </Stack>
          {run && (
            <Chip size="small" variant="outlined" sx={{ alignSelf: { md: 'center' } }}
                  color={run.status === 'DONE' ? 'success' : run.status === 'FAILED' ? 'error' : 'primary'}
                  label={`Last run: ${run.date_from} → ${run.date_to} · ${run.status.toLowerCase()}`} />
          )}
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip key={p.label} label={p.label} size="small" variant={from === p.from && to === p.to ? 'filled' : 'outlined'}
                  color="primary" onClick={() => { setFrom(p.from); setTo(p.to); }} disabled={running} />
          ))}
        </Stack>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={6} md={4}>
            <TextField fullWidth size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)}
                       InputLabelProps={{ shrink: true }} disabled={running} />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)}
                       InputLabelProps={{ shrink: true }} inputProps={{ max: iso(daysAgo(1)) }} disabled={running} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button fullWidth variant="contained" startIcon={<PlayArrowRounded />} onClick={start} disabled={running || starting}>
              {running ? 'Running…' : 'Run backtest'}
            </Button>
          </Grid>
        </Grid>

        {running && run && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: t.text2 }}>{run.message || 'Starting…'}</Typography>
              <Typography variant="body2" sx={{ color: t.text2 }}>{run.progress}%</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={run.progress} aria-label="Backtest progress" />
            <Typography variant="caption" sx={{ color: t.text3, display: 'block', mt: 0.75 }}>
              First run downloads ~10 years of daily candles for 200 stocks from Kite (2–4 minutes, needs today's Kite login); later runs reuse them.
            </Typography>
          </Box>
        )}
        {run?.status === 'DONE' && !running && <Alert severity="success" sx={{ mt: 2 }}>{run.message}. Results below, charges and slippage included.</Alert>}
        {run?.status === 'FAILED' && <Alert severity="error" sx={{ mt: 2 }}>{run.message}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <Typography variant="caption" sx={{ color: t.text3, display: 'block', mt: 1.5 }}>
          Pass mark before LIVE: on BOTH the design period and the hold-out period, CAGR above NIFTY 50 and a smaller max drawdown.
          Limits: today's NIFTY 200 list is used for past dates (survivorship bias flatters results); fills at the day's open.
        </Typography>
      </Panel>
    </Box>
  );
};

export default BacktestPanel;
